import { last } from 'lodash';
import { common } from '@gosupersimple/types';

import { ensureUniqueFieldKey } from '@/core/operation';

import { getTimeAggregationPeriodLabel } from '../pipeline/time-label';
import { Field, Grouping, TimeAggregationPeriod } from '../types';
import { FieldGroup, isField, isFieldGroup } from '../pipeline/utils';

const DefaultTimePrecision: TimeAggregationPeriod = 'month' as const;

const isRelatedModelField = (field: Field): boolean =>
  field.relation !== undefined &&
  field.model !== undefined &&
  field.model.modelId !== field.relation.baseModelId;

// The default grouping granularity for a Date field is the precision declared
// on the semantic model field (carried through on `field.precision`), falling
// back to month. `day_of_week` is never used as an implicit default.
const getDefaultGroupingPrecision = (field: Field): TimeAggregationPeriod =>
  field.precision !== undefined && field.precision !== 'day_of_week'
    ? field.precision
    : DefaultTimePrecision;

export const createGroupingFromField = (field: Field, fields: Field[]): Grouping => ({
  key: field.key,
  ...(field.type === 'Date' ? { precision: getDefaultGroupingPrecision(field) } : {}),
  ...(isRelatedModelField(field)
    ? {
        relation: { key: field.relation!.key, modelId: field.relation!.baseModelId },
        property: { key: ensureUniqueFieldKey(fields, field.key), name: field.relation!.name },
      }
    : {}),
});

export const sortDateGroupingsLast = (groups: Grouping[]): Grouping[] =>
  [...groups].sort((a, b) => {
    const aHasPrecision = Boolean(a.precision);
    const bHasPrecision = Boolean(b.precision);
    return aHasPrecision === bHasPrecision ? 0 : aHasPrecision && !bHasPrecision ? 1 : -1;
  });

export const fillGroupsForTimeSeriesQuery = (groups: Grouping[], fields: Field[]): Grouping[] => {
  const lastGroup = last(groups);
  if (lastGroup) {
    const field = fields.find((f) => f.key === lastGroup.key);
    if (field?.type === 'Date') {
      return groups.map((group, i) => ({
        ...group,
        fill: true,
        ...(i === groups.length - 1 && { precision: group.precision ?? 'month' }),
      }));
    }
  }
  return groups;
};

export const ensureValidGroupings = (groupings: Grouping[], fields: Field[]): Grouping[] =>
  groupings.filter(
    (grouping) =>
      ('relation' in grouping && grouping.relation !== undefined) ||
      fields.some((f) => f.key === grouping.key),
  );

export const getGroupableFields = (
  fields: (Field | FieldGroup)[],
  existingGroupings: Grouping[],
) => {
  const isFieldGroupable = (field: Field | FieldGroup) =>
    isField(field) && !existingGroupings.some(({ key }) => key === field.key);

  return fields
    .map((fieldOrGroup) =>
      isFieldGroup(fieldOrGroup)
        ? { ...fieldOrGroup, fields: fieldOrGroup.fields.filter(isFieldGroupable) }
        : fieldOrGroup,
    )
    .filter((fieldOrGroup) =>
      isField(fieldOrGroup) ? isFieldGroupable(fieldOrGroup) : fieldOrGroup.fields.length > 0,
    );
};

export const timePrecisionOptions = common.timeInterval.options.map(({ value }) => ({
  value,
  label: getTimeAggregationPeriodLabel(value),
}));

export const setFillForDates = (groups: Grouping[]): Grouping[] =>
  groups.map((group) => ({ ...group, fill: group.precision !== undefined }));
